import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Dialog, DialogContent, DialogTitle } from "./ui/dialog";
import { Button } from "./ui/button";
import { Trophy, Sparkles, ArrowRight, UserPlus, Star, RotateCcw, Eye, PenSquare, Gem } from "lucide-react";
import { GEM_BONUS } from "./nav/navShared";

// Confetti colors matching our theme
const CONFETTI_COLORS = ["#0EA5E9", "#F97316", "#22C55E", "#FBBF24", "#A855F7"];

export function GemBonusLine({ gemBonus, className = "mt-2" }) {
  if (!(gemBonus > 0)) return null;
  const count = Math.max(1, Math.round(gemBonus / GEM_BONUS));
  return (
    <p className={`text-emerald-600 dark:text-emerald-400 font-medium flex items-center justify-center gap-1.5 ${className}`} data-testid="success-gem-bonus">
      <Gem className="w-4 h-4" /> {count === 1 ? "Hidden Gem" : `${count} Hidden Gems`} +{gemBonus} included!
    </p>
  );
}

function Confetti() {
  const [particles, setParticles] = useState([]);

  useEffect(() => {
    const newParticles = Array.from({ length: 50 }, (_, i) => ({
      id: i,
      x: Math.random() * 100,
      delay: Math.random() * 0.5,
      duration: 2 + Math.random() * 2,
      color: CONFETTI_COLORS[Math.floor(Math.random() * CONFETTI_COLORS.length)],
      size: 6 + Math.random() * 8,
    }));
    setParticles(newParticles);
  }, []);

  return (
    <div className="confetti-container">
      {particles.map((particle) => (
        <motion.div
          key={particle.id}
          className="confetti"
          initial={{ 
            x: `${particle.x}vw`, 
            y: -20, 
            rotate: 0,
            opacity: 1 
          }}
          animate={{ 
            y: "110vh", 
            rotate: 720,
            opacity: 0 
          }}
          transition={{
            duration: particle.duration,
            delay: particle.delay,
            ease: "linear",
          }}
          style={{
            backgroundColor: particle.color,
            width: particle.size,
            height: particle.size,
            borderRadius: Math.random() > 0.5 ? "50%" : "2px",
          }}
        />
      ))}
    </div>
  );
}

export default function SuccessModal({ 
  isOpen, 
  onClose, 
  onContinue, 
  showChallenges, 
  user, 
  pointsEarned = 10,
  isChallenge = false,
  alreadyAnswered = false,
  sessionScore = null,
  streakBonus = 0,
  answerRevealed = false,
  blitzBonus = 0,
  gemBonus = 0,
  challengesCompleted = 0,
  challengeLimit = 10,
  subscriptionTier = "free",
  onCreateAccount = null
}) {
  const navigate = useNavigate();
  const isSubscriber = subscriptionTier === "student" || subscriptionTier === "school";
  const reachedDailyLimit = challengesCompleted >= challengeLimit;
  
  const handleCreateAccount = () => {
    // Close the success modal and trigger the auth modal callback
    onClose();
    if (onCreateAccount) {
      onCreateAccount();
    }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          <Confetti />
          <Dialog open={isOpen} onOpenChange={onClose}>
            <DialogContent className="sm:max-w-md text-center" data-testid="success-modal">
              <DialogTitle className="sr-only">Success</DialogTitle>
              <motion.div
                initial={{ scale: 0.8, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={{ 
                  type: "spring", 
                  stiffness: 300, 
                  damping: 20 
                }}
                className="py-6"
              >
                {/* Trophy Icon */}
                <motion.div
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  transition={{ 
                    delay: 0.2, 
                    type: "spring", 
                    stiffness: 400 
                  }}
                  className="mx-auto mb-6 p-4 rounded-full bg-secondary/10 w-fit"
                >
                  <Trophy className="w-16 h-16 text-secondary" />
                </motion.div>

                {/* Good Job Text */}
                <motion.div
                  initial={{ y: 20, opacity: 0 }}
                  animate={{ y: 0, opacity: 1 }}
                  transition={{ delay: 0.3 }}
                >
                  <h2 className="font-heading text-3xl sm:text-4xl font-bold mb-2 flex items-center justify-center gap-2">
                    <Sparkles className="w-6 h-6 text-secondary" />
                    <span className="text-gradient">Good Job!</span>
                    <Sparkles className="w-6 h-6 text-secondary" />
                  </h2>
                  
                  {/* Points Display - Different for logged in vs not, and already answered */}
                  {user ? (
                    <motion.div
                      initial={{ scale: 0.8, opacity: 0 }}
                      animate={{ scale: 1, opacity: 1 }}
                      transition={{ delay: 0.4 }}
                      className="mt-4 mb-2"
                    >
                      {answerRevealed ? (
                        <>
                          <div className="inline-flex items-center gap-2 px-4 py-2 bg-muted rounded-full">
                            <Eye className="w-5 h-5 text-muted-foreground" />
                            <span className="text-lg font-semibold text-muted-foreground">Revealed Answer</span>
                          </div>
                          <p className="text-muted-foreground mt-2">
                            0 points (answer was revealed)
                          </p>
                        </>
                      ) : alreadyAnswered ? (
                        <>
                          <div className="inline-flex items-center gap-2 px-4 py-2 bg-muted rounded-full">
                            <RotateCcw className="w-5 h-5 text-muted-foreground" />
                            <span className="text-lg font-semibold text-muted-foreground">Practice Run</span>
                          </div>
                          <p className="text-muted-foreground mt-2" data-testid="success-points-practice">
                            +{pointsEarned} points (previously completed{gemBonus > 0 ? ` +${pointsEarned - gemBonus}, hidden gems +${gemBonus}` : ""})
                          </p>
                          <GemBonusLine gemBonus={gemBonus} />
                        </>
                      ) : (
                        <>
                          <div className="inline-flex items-center gap-2 px-4 py-2 bg-secondary/10 rounded-full">
                            <Star className="w-5 h-5 text-secondary fill-secondary" />
                            <span className="text-lg font-bold text-secondary" data-testid="success-points">+{pointsEarned} points</span>
                          </div>
                          {blitzBonus > 0 && (
                            <p className="text-primary font-medium mt-2" data-testid="success-blitz-bonus">
                              ⚡ Blitz Bonus +{blitzBonus} included!
                            </p>
                          )}
                          {streakBonus > 0 && (
                            <p className="text-success font-medium mt-2">
                              🔥 Streak Bonus +{streakBonus} included!
                            </p>
                          )}
                          <GemBonusLine gemBonus={gemBonus} />
                          <p className="text-muted-foreground mt-2">
                            Added to your profile!
                          </p>
                        </>
                      )}
                    </motion.div>
                  ) : (
                    <motion.div
                      initial={{ y: 10, opacity: 0 }}
                      animate={{ y: 0, opacity: 1 }}
                      transition={{ delay: 0.4 }}
                      className="mt-4"
                    >
                      {answerRevealed ? (
                        <>
                          <div className="inline-flex items-center gap-2 px-4 py-2 bg-muted rounded-full mb-2">
                            <Eye className="w-5 h-5 text-muted-foreground" />
                            <span className="text-lg font-semibold text-muted-foreground">Revealed Answer</span>
                          </div>
                          <p className="text-muted-foreground mb-2">
                            0 points (answer was revealed)
                          </p>
                        </>
                      ) : (
                        <>
                          <div className="inline-flex items-center gap-2 px-4 py-2 bg-muted rounded-full mb-2">
                            <Star className="w-5 h-5 text-secondary" />
                            <span className="text-lg font-semibold">+{pointsEarned} points!</span>
                          </div>
                          {blitzBonus > 0 && (
                            <p className="text-primary font-medium mb-2">
                              ⚡ Blitz Bonus +{blitzBonus} included!
                            </p>
                          )}
                          {streakBonus > 0 && (
                            <p className="text-success font-medium mb-2">
                              🔥 Streak Bonus +{streakBonus} included!
                            </p>
                          )}
                          <GemBonusLine gemBonus={gemBonus} className="mb-2" />
                          {alreadyAnswered && (
                            <p className="text-xs text-muted-foreground mb-2">
                              (Practice run - previously answered)
                            </p>
                          )}
                        </>
                      )}
                      {sessionScore !== null && sessionScore > 0 && (
                        <p className="text-sm text-muted-foreground mb-2">
                          Session Total: <span className="font-medium text-secondary">{sessionScore} pts</span>
                        </p>
                      )}
                      <p className="text-muted-foreground">
                        Create an account to keep your score.
                      </p>
                    </motion.div>
                  )}
                </motion.div>

                {/* Action Buttons */}
                <motion.div
                  initial={{ y: 20, opacity: 0 }}
                  animate={{ y: 0, opacity: 1 }}
                  transition={{ delay: 0.5 }}
                  className="mt-8 space-y-3"
                >
                  {user ? (
                    // Logged in user buttons
                    <>
                      {showChallenges ? (
                        <Button
                          onClick={onContinue}
                          className="gap-2 rounded-xl px-8 h-12 bg-secondary hover:bg-secondary/90"
                          data-testid="continue-challenges-btn"
                        >
                          Continue to Challenges
                          <ArrowRight className="w-4 h-4" />
                        </Button>
                      ) : reachedDailyLimit && isSubscriber ? (
                        // Show create question button when subscriber reaches limit
                        <div className="space-y-3">
                          <p className="text-sm text-muted-foreground mb-2">
                            You've completed all {challengeLimit} challenges for today!
                          </p>
                          <Button
                            onClick={() => { onClose(); navigate('/create-question'); }}
                            className="gap-2 rounded-xl px-8 h-12 bg-gradient-to-r from-amber-500 to-orange-500 hover:opacity-90"
                            data-testid="create-question-btn"
                          >
                            <PenSquare className="w-4 h-4" />
                            Create Your Own Question
                          </Button>
                          <Button
                            onClick={onClose}
                            variant="outline"
                            className="gap-2 rounded-xl px-8 h-12"
                            data-testid="close-success-btn"
                          >
                            Done for Today
                          </Button>
                        </div>
                      ) : (
                        <Button
                          onClick={onClose}
                          className="gap-2 rounded-xl px-8 h-12 bg-primary hover:bg-primary/90"
                          data-testid="close-success-btn"
                        >
                          Awesome!
                        </Button>
                      )}
                    </>
                  ) : (
                    // Not logged in - show create account option
                    <>
                      <Button
                        onClick={handleCreateAccount}
                        className="gap-2 rounded-xl px-8 h-12 bg-secondary hover:bg-secondary/90 w-full sm:w-auto"
                        data-testid="create-account-btn"
                      >
                        <UserPlus className="w-4 h-4" />
                        Create Account & Save Score
                      </Button>
                      
                      {showChallenges ? (
                        <Button
                          onClick={onContinue}
                          variant="outline"
                          className="gap-2 rounded-xl px-8 h-12 w-full sm:w-auto"
                          data-testid="continue-challenges-btn"
                        >
                          Continue to Challenges
                          <ArrowRight className="w-4 h-4" />
                        </Button>
                      ) : (
                        <Button
                          onClick={onClose}
                          variant="ghost"
                          className="gap-2 rounded-xl px-8 h-10"
                          data-testid="close-success-btn"
                        >
                          Maybe Later
                        </Button>
                      )}
                    </>
                  )}
                </motion.div>
              </motion.div>
            </DialogContent>
          </Dialog>
        </>
      )}
    </AnimatePresence>
  );
}
